/**
 * A QR code, generated on the device.
 *
 * It has to be generated here and it cannot be fetched: the technician's phone is often the
 * one without signal, and the whole point of the payment link is that the **customer's** phone
 * has one. A code that needs the network to be drawn is a code that fails in exactly the
 * driveway it exists for.
 *
 * So this is a small, complete encoder rather than a dependency: byte mode, error correction
 * level M, versions 1 through 10 — up to 213 bytes, which is a comfortable margin over a
 * payment URL. Level M because a phone screen in daylight is a poor scanning surface and 15%
 * recovery is what makes the difference; the larger levels would cost modules for no gain
 * against a screen that is not going to be creased or rained on.
 *
 * Everything below is the specification (ISO/IEC 18004) and nothing is invented. It is written
 * out rather than pulled in because an incorrect QR code is worse than no QR code — it sends a
 * customer to a page that is not theirs — and a hundred lines that can be checked against the
 * standard is a better answer than a black box in a payment path.
 */

/** Data codewords and block structure at level M, by version. */
const M_BLOCKS: Record<number, { ecPerBlock: number; groups: Array<[blocks: number, dataCodewords: number]> }> = {
  1: { ecPerBlock: 10, groups: [[1, 16]] },
  2: { ecPerBlock: 16, groups: [[1, 28]] },
  3: { ecPerBlock: 26, groups: [[1, 44]] },
  4: { ecPerBlock: 18, groups: [[2, 32]] },
  5: { ecPerBlock: 24, groups: [[2, 43]] },
  6: { ecPerBlock: 16, groups: [[4, 27]] },
  7: { ecPerBlock: 18, groups: [[4, 31]] },
  8: { ecPerBlock: 22, groups: [[2, 38], [2, 39]] },
  9: { ecPerBlock: 22, groups: [[3, 36], [2, 37]] },
  10: { ecPerBlock: 26, groups: [[4, 43], [1, 44]] },
};

/** Where the alignment patterns sit, by version. Version 1 has none. */
const ALIGNMENT: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
};

// --- GF(256) ---------------------------------------------------------------
// The field the Reed-Solomon codewords live in, generated once with the QR
// primitive polynomial x^8 + x^4 + x^3 + x^2 + 1.

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let value = 1;
  for (let index = 0; index < 255; index += 1) {
    EXP[index] = value;
    LOG[value] = index;
    value <<= 1;
    if (value & 0x100) value ^= 0x11d;
  }
  for (let index = 255; index < 512; index += 1) EXP[index] = EXP[index - 255]!;
})();

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!);

/** The generator polynomial for `degree` error-correction codewords. */
function generator(degree: number): number[] {
  let poly = [1];
  for (let index = 0; index < degree; index += 1) {
    const next = new Array<number>(poly.length + 1).fill(0) as number[];
    for (let position = 0; position < poly.length; position += 1) {
      next[position] = next[position]! ^ mul(poly[position]!, EXP[index]!);
      next[position + 1] = next[position + 1]! ^ poly[position]!;
    }
    poly = next;
  }
  return poly;
}

/** Polynomial division: the remainder is the error-correction block. */
function remainder(data: number[], degree: number): number[] {
  const gen = generator(degree);
  const buffer = [...data, ...new Array<number>(degree).fill(0)];
  for (let index = 0; index < data.length; index += 1) {
    const factor = buffer[index]!;
    if (factor === 0) continue;
    for (let position = 0; position < gen.length; position += 1) {
      buffer[index + position] = buffer[index + position]! ^ mul(gen[position]!, factor);
    }
  }
  return buffer.slice(data.length);
}

// --- BCH -------------------------------------------------------------------

function bch(value: number, poly: number, bits: number): number {
  let result = value << bits;
  const polyBits = 32 - Math.clz32(poly);
  while (32 - Math.clz32(result) >= polyBits) {
    result ^= poly << (32 - Math.clz32(result) - polyBits);
  }
  return result;
}

/** The 15-bit format string: two bits of level, three of mask, ten of BCH, then masked. */
function formatBits(mask: number): number {
  // Level M is `00`.
  const data = mask & 0b111;
  return ((data << 10) | bch(data, 0b10100110111, 10)) ^ 0b101010000010010;
}

/** The 18-bit version string, on versions 7 and up. */
function versionBits(version: number): number {
  return (version << 12) | bch(version, 0b1111100100101, 12);
}

// --- Encoding --------------------------------------------------------------

class Bits {
  private readonly bits: number[] = [];

  push(value: number, length: number): void {
    for (let index = length - 1; index >= 0; index -= 1) this.bits.push((value >>> index) & 1);
  }

  get length(): number {
    return this.bits.length;
  }

  toCodewords(): number[] {
    const out: number[] = [];
    for (let index = 0; index < this.bits.length; index += 8) {
      let byte = 0;
      for (let offset = 0; offset < 8; offset += 1) byte = (byte << 1) | (this.bits[index + offset] ?? 0);
      out.push(byte);
    }
    return out;
  }
}

function capacityBytes(version: number): number {
  const spec = M_BLOCKS[version]!;
  const dataCodewords = spec.groups.reduce((total, [blocks, count]) => total + blocks * count, 0);
  const countBits = version < 10 ? 8 : 16;
  return dataCodewords - Math.ceil((4 + countBits) / 8);
}

function pickVersion(byteLength: number): number {
  for (let version = 1; version <= 10; version += 1) {
    if (byteLength <= capacityBytes(version)) return version;
  }
  throw new Error('qr_too_long');
}

/**
 * The finished module grid.
 *
 * `true` is a dark module. The caller draws it; nothing here knows about pixels, so the same
 * grid renders as SVG on screen and as anything else later.
 */
export type QrMatrix = { size: number; modules: boolean[][] };

export function encodeQr(text: string): QrMatrix {
  const bytes = [...new TextEncoder().encode(text)];
  const version = pickVersion(bytes.length);
  const spec = M_BLOCKS[version]!;
  const dataCodewords = spec.groups.reduce((total, [blocks, count]) => total + blocks * count, 0);

  // ---- the bit stream ----
  const stream = new Bits();
  stream.push(0b0100, 4); // byte mode
  stream.push(bytes.length, version < 10 ? 8 : 16);
  for (const byte of bytes) stream.push(byte, 8);

  const capacityBits = dataCodewords * 8;
  stream.push(0, Math.min(4, capacityBits - stream.length)); // terminator
  if (stream.length % 8) stream.push(0, 8 - (stream.length % 8)); // to a byte boundary

  const codewords = stream.toCodewords();
  // The two pad bytes the standard names, alternating until the block is full.
  for (let index = 0; codewords.length < dataCodewords; index += 1) codewords.push(index % 2 === 0 ? 0xec : 0x11);

  // ---- interleave the blocks ----
  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const [blocks, count] of spec.groups) {
    for (let block = 0; block < blocks; block += 1) {
      const chunk = codewords.slice(offset, offset + count);
      offset += count;
      dataBlocks.push(chunk);
      ecBlocks.push(remainder(chunk, spec.ecPerBlock));
    }
  }

  const interleaved: number[] = [];
  const longest = Math.max(...dataBlocks.map((block) => block.length));
  for (let index = 0; index < longest; index += 1) {
    for (const block of dataBlocks) if (index < block.length) interleaved.push(block[index]!);
  }
  for (let index = 0; index < spec.ecPerBlock; index += 1) {
    for (const block of ecBlocks) interleaved.push(block[index]!);
  }

  // ---- the grid ----
  const size = version * 4 + 17;
  const modules: (boolean | null)[][] = Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null));
  const reserved: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

  const set = (row: number, column: number, dark: boolean, keep = true) => {
    modules[row]![column] = dark;
    if (keep) reserved[row]![column] = true;
  };

  // Finder patterns and their separators.
  const finder = (top: number, left: number) => {
    for (let row = -1; row <= 7; row += 1) {
      for (let column = -1; column <= 7; column += 1) {
        const r = top + row;
        const c = left + column;
        if (r < 0 || r >= size || c < 0 || c >= size) continue;
        const inRing = row >= 0 && row <= 6 && (column === 0 || column === 6);
        const inColumn = column >= 0 && column <= 6 && (row === 0 || row === 6);
        const inCore = row >= 2 && row <= 4 && column >= 2 && column <= 4;
        set(r, c, inRing || inColumn || inCore);
      }
    }
  };
  finder(0, 0);
  finder(0, size - 7);
  finder(size - 7, 0);

  // Timing patterns.
  for (let index = 8; index < size - 8; index += 1) {
    set(6, index, index % 2 === 0);
    set(index, 6, index % 2 === 0);
  }

  // Alignment patterns, skipping the three that would sit on a finder.
  const centres = ALIGNMENT[version]!;
  for (const row of centres) {
    for (const column of centres) {
      const onFinder =
        (row === 6 && column === 6) || (row === 6 && column === size - 7) || (row === size - 7 && column === 6);
      if (onFinder) continue;
      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          set(row + dr, column + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
        }
      }
    }
  }

  // The dark module, and the format areas held back until the mask is chosen.
  set(size - 8, 8, true);
  for (let index = 0; index < 9; index += 1) {
    if (modules[8]![index] === null) set(8, index, false);
    if (modules[index]![8] === null) set(index, 8, false);
  }
  for (let index = 0; index < 8; index += 1) {
    if (modules[8]![size - 1 - index] === null) set(8, size - 1 - index, false);
    if (modules[size - 1 - index]![8] === null) set(size - 1 - index, 8, false);
  }

  if (version >= 7) {
    const bits = versionBits(version);
    for (let index = 0; index < 18; index += 1) {
      const bit = ((bits >>> index) & 1) === 1;
      set(Math.floor(index / 3), size - 11 + (index % 3), bit);
      set(size - 11 + (index % 3), Math.floor(index / 3), bit);
    }
  }

  // ---- the data, in a two-column zigzag from the bottom right ----
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the vertical timing column is skipped entirely
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const column of [right, right - 1]) {
        if (reserved[row]![column]) continue;
        const byte = interleaved[bitIndex >>> 3] ?? 0;
        const bit = ((byte >>> (7 - (bitIndex & 7))) & 1) === 1;
        modules[row]![column] = bit;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }

  // ---- choose the mask ----
  let best: { mask: number; grid: boolean[][]; penalty: number } | null = null;
  for (let mask = 0; mask < 8; mask += 1) {
    const grid = applyMask(modules as boolean[][], reserved, mask, size);
    writeFormat(grid, mask, size);
    const score = penalty(grid, size);
    if (!best || score < best.penalty) best = { mask, grid, penalty: score };
  }

  return { size, modules: best!.grid };
}

function applyMask(modules: boolean[][], reserved: boolean[][], mask: number, size: number): boolean[][] {
  const rule = (row: number, column: number): boolean => {
    switch (mask) {
      case 0: return (row + column) % 2 === 0;
      case 1: return row % 2 === 0;
      case 2: return column % 3 === 0;
      case 3: return (row + column) % 3 === 0;
      case 4: return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0;
      case 5: return ((row * column) % 2) + ((row * column) % 3) === 0;
      case 6: return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0;
      default: return (((row + column) % 2) + ((row * column) % 3)) % 2 === 0;
    }
  };

  const grid = modules.map((row) => [...row]);
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      if (reserved[row]![column]) continue;
      if (rule(row, column)) grid[row]![column] = !grid[row]![column];
    }
  }
  return grid;
}

function writeFormat(grid: boolean[][], mask: number, size: number): void {
  const bits = formatBits(mask);
  for (let index = 0; index < 15; index += 1) {
    const bit = ((bits >>> index) & 1) === 1;
    // The copy beside the top-left finder, running down then right.
    if (index < 6) grid[index]![8] = bit;
    else if (index < 8) grid[index + 1]![8] = bit;
    else if (index === 8) grid[8]![7] = bit;
    else grid[8]![14 - index] = bit;

    // …and the second copy, split between the other two finders.
    if (index < 8) grid[8]![size - 1 - index] = bit;
    else grid[size - 15 + index]![8] = bit;
  }
  grid[size - 8]![8] = true;
}

/** The four penalty rules, which is how the standard picks between the eight masks. */
function penalty(grid: boolean[][], size: number): number {
  let score = 0;

  // Runs of five or more.
  for (let row = 0; row < size; row += 1) {
    for (const horizontal of [true, false]) {
      let run = 1;
      for (let index = 1; index < size; index += 1) {
        const current = horizontal ? grid[row]![index] : grid[index]![row];
        const previous = horizontal ? grid[row]![index - 1] : grid[index - 1]![row];
        if (current === previous) run += 1;
        else {
          if (run >= 5) score += run - 2;
          run = 1;
        }
      }
      if (run >= 5) score += run - 2;
    }
  }

  // Two-by-two blocks of one colour.
  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const value = grid[row]![column];
      if (value === grid[row]![column + 1] && value === grid[row + 1]![column] && value === grid[row + 1]![column + 1]) {
        score += 3;
      }
    }
  }

  // The finder-like pattern, which a scanner would mistake for one.
  const pattern = [true, false, true, true, true, false, true, false, false, false, false];
  const reversed = [...pattern].reverse();
  const matches = (line: boolean[], start: number, want: boolean[]): boolean =>
    want.every((bit, offset) => line[start + offset] === bit);

  for (let index = 0; index < size; index += 1) {
    const row = grid[index]!;
    const column = grid.map((line) => line[index]!);
    for (let start = 0; start + 11 <= size; start += 1) {
      if (matches(row, start, pattern) || matches(row, start, reversed)) score += 40;
      if (matches(column, start, pattern) || matches(column, start, reversed)) score += 40;
    }
  }

  // The balance of dark to light.
  const dark = grid.flat().filter(Boolean).length;
  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

  return score;
}

/**
 * The grid as an SVG path.
 *
 * One path rather than a rectangle per module: a version-6 code is 41×41 and a thousand
 * elements is a noticeable pause on the oldest phone in the fleet.
 */
export function qrSvgPath(matrix: QrMatrix): string {
  const parts: string[] = [];
  for (let row = 0; row < matrix.size; row += 1) {
    for (let column = 0; column < matrix.size; column += 1) {
      if (matrix.modules[row]![column]) parts.push(`M${column} ${row}h1v1h-1z`);
    }
  }
  return parts.join('');
}
